<template>
  <main dir="rtl" class="mx-auto max-w-screen-2xl space-y-8 px-4 py-6 sm:px-6 lg:px-8">
    <header class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p class="text-sm font-semibold text-cyan-700">SayyarPay / مدیریت</p>
        <h1 class="mt-1 text-2xl font-bold text-slate-900 sm:text-3xl">داشبورد مدیریت</h1>
        <p class="mt-2 text-sm text-slate-500">نمای کلی کاربران، تراکنش‌ها و کارمزدها</p>
      </div>
      <button
        type="button"
        class="inline-flex items-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="dashboardStatsLoading"
        @click="loadDashboardStats"
      >
        <v-icon size="18" :class="{ 'animate-spin': dashboardStatsLoading }">mdi-refresh</v-icon>
        به‌روزرسانی آمار
      </button>
    </header>

    <section aria-labelledby="dashboard-stats-title" class="space-y-4">
      <div class="flex items-center gap-3">
        <span class="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-50 text-cyan-700">
          <v-icon size="21">mdi-chart-box-outline</v-icon>
        </span>
        <div>
          <h2 id="dashboard-stats-title" class="text-lg font-bold text-slate-800">آمار کلی</h2>
          <p class="text-xs text-slate-500">تراکنش‌ها از ExternalTransactions محاسبه می‌شوند</p>
        </div>
      </div>

      <div v-if="dashboardStatsLoading" class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" role="status">
        <div v-for="index in 8" :key="index" class="h-32 animate-pulse rounded-2xl bg-slate-100"></div>
        <span class="sr-only">در حال بارگذاری آمار داشبورد</span>
      </div>

      <div v-else-if="dashboardStatsError" class="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <span>{{ dashboardStatsError }}</span>
          <button type="button" class="font-semibold underline" @click="loadDashboardStats">تلاش دوباره</button>
        </div>
      </div>

      <div v-else class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <WalletCard
          v-for="metric in dashboardMetrics"
          :key="metric.key"
          :title="metric.title"
          :value="metric.value"
          :type="metric.type"
          :unit="metric.unit"
          :icon="metric.icon"
        />
      </div>
    </section>

    <section aria-labelledby="manual-charges-title" class="space-y-4">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <span class="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700">
            <v-icon size="21">mdi-cash-multiple</v-icon>
          </span>
          <div>
            <h2 id="manual-charges-title" class="text-lg font-bold text-slate-800">آمار شارژهای دستی</h2>
            <p class="text-xs text-slate-500">وضعیت شارژهای ریالی و تتری</p>
          </div>
        </div>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="manualChargeStatsLoading"
          @click="loadManualChargeStats"
        >
          <v-icon size="18" :class="{ 'animate-spin': manualChargeStatsLoading }">mdi-refresh</v-icon>
          تازه‌سازی
        </button>
      </div>

      <div v-if="manualChargeStatsLoading" class="rounded-2xl bg-white p-8 text-center text-sm text-slate-500 shadow-sm ring-1 ring-slate-200" role="status">
        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
        <p class="mt-3">در حال دریافت آمار شارژهای دستی...</p>
      </div>

      <div v-else-if="manualChargeStatsError" class="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="alert">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <span>{{ manualChargeStatsError }}</span>
          <button type="button" class="font-semibold underline" @click="loadManualChargeStats">تلاش دوباره</button>
        </div>
      </div>

      <div v-else-if="manualChargeStats" class="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <article
          v-for="metric in manualChargeMetrics"
          :key="metric.key"
          class="flex min-h-36 min-w-0 flex-col justify-between rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="text-sm font-semibold text-slate-600">{{ metric.title }}</p>
              <p class="mt-1 text-xs text-slate-400">{{ metric.count }} تراکنش</p>
            </div>
            <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl" :class="metric.iconClasses">
              <v-icon size="20">{{ metric.icon }}</v-icon>
            </span>
          </div>
          <p class="mt-5 break-all text-2xl font-bold text-slate-900">
            {{ metric.amount }}
            <span class="text-xs font-medium text-slate-500">{{ metric.unit }}</span>
          </p>
        </article>
      </div>

      <div v-else class="rounded-2xl bg-white p-6 text-center text-sm text-slate-500 shadow-sm ring-1 ring-slate-200">
        آماری برای نمایش وجود ندارد.
      </div>

      <button
        type="button"
        class="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-cyan-800"
        @click="goToManualCharges"
      >
        مشاهده جزئیات شارژهای دستی
        <v-icon size="18">mdi-arrow-left</v-icon>
      </button>
    </section>
  </main>
</template>

<script>
import WalletCard from '@/components/appComponents/WalletCard.vue'
import store from '@/store'

export default {
  name: 'ServicesPage',
  components: {
    WalletCard
  },
  data() {
    return {
      dashboardStats: null,
      dashboardStatsLoading: true,
      dashboardStatsError: null,
      manualChargeStats: null,
      manualChargeStatsLoading: true,
      manualChargeStatsError: null
    }
  },
  computed: {
    dashboardMetrics() {
      if (!this.dashboardStats) return []
      const { users, transactions, commissions } = this.dashboardStats
      return [
        { key: 'total-users', title: 'کل کاربران', value: users.totalUsers, type: 'User', unit: 'نفر', icon: 'mdi-account-group' },
        { key: 'pending-verification', title: 'در انتظار احراز هویت', value: users.pendingVerification, type: 'User', unit: 'نفر', icon: 'mdi-account-clock' },
        { key: 'total-transactions', title: 'کل تراکنش‌ها', value: transactions.totalTransactions, type: 'Count', unit: 'تراکنش', icon: 'mdi-swap-horizontal' },
        { key: 'successful-transactions', title: 'تراکنش‌های موفق', value: transactions.successfulTransactions, type: 'Count', unit: 'تراکنش', icon: 'mdi-check-circle-outline' },
        { key: 'today-volume', title: 'مبلغ تراکنش موفق امروز', value: transactions.todayRialiAmount, type: 'Wallet', unit: 'ریال', icon: 'mdi-calendar-today' },
        { key: 'yesterday-volume', title: 'مبلغ تراکنش موفق دیروز', value: transactions.yesterdayRialiAmount, type: 'Wallet', unit: 'ریال', icon: 'mdi-calendar-clock' },
        { key: 'total-volume', title: 'مجموع تراکنش‌های موفق ریالی', value: transactions.successfulRialiAmount, type: 'Wallet', unit: 'ریال', icon: 'mdi-chart-timeline-variant' },
        { key: 'today-commissions', title: 'کارمزد ریالی امروز', value: commissions.todayRiali, type: 'Wallet', unit: 'ریال', icon: 'mdi-percent-outline' },
        { key: 'yesterday-commissions', title: 'کارمزد ریالی دیروز', value: commissions.yesterdayRiali, type: 'Wallet', unit: 'ریال', icon: 'mdi-percent' },
        { key: 'total-commissions', title: 'مجموع کارمزد ریالی', value: commissions.totalRiali, type: 'Wallet', unit: 'ریال', icon: 'mdi-cash-multiple' },
        { key: 'usdt-commissions', title: 'مجموع کارمزد تتری', value: commissions.totalUsdt, type: 'USDT', unit: 'USDT', icon: 'mdi-currency-usd' }
      ]
    },
    manualChargeMetrics() {
      const stats = this.manualChargeStats
      if (!stats) return []
      return [
        {
          key: 'riali',
          title: 'شارژ ریالی موفق',
          count: stats.rialiStats.successfulCharges,
          amount: this.formatShortAmount(stats.rialiStats.totalAmountFormatted),
          unit: 'ریال',
          icon: 'mdi-cash-multiple',
          iconClasses: 'bg-emerald-50 text-emerald-700'
        },
        {
          key: 'crypto',
          title: 'شارژ تتری موفق',
          count: stats.cryptoStats.successfulCharges,
          amount: this.formatShortAmount(stats.cryptoStats.totalUSDTAmountFormatted),
          unit: 'USDT',
          icon: 'mdi-currency-usd',
          iconClasses: 'bg-violet-50 text-violet-700'
        },
        {
          key: 'today',
          title: 'شارژ موفق امروز',
          count: stats.todayStats?.totalCharges || 0,
          amount: this.formatShortAmount(stats.todayStats?.grandTotalFormatted || '0'),
          unit: 'ریال',
          icon: 'mdi-calendar-today',
          iconClasses: 'bg-sky-50 text-sky-700'
        },
        {
          key: 'yesterday',
          title: 'شارژ موفق دیروز',
          count: stats.yesterdayStats?.totalCharges || 0,
          amount: this.formatShortAmount(stats.yesterdayStats?.grandTotalFormatted || '0'),
          unit: 'ریال',
          icon: 'mdi-calendar-clock',
          iconClasses: 'bg-amber-50 text-amber-700'
        }
      ]
    }
  },
  mounted() {
    this.loadDashboardStats()
    this.loadManualChargeStats()
  },
  methods: {
    async loadDashboardStats() {
      this.dashboardStatsLoading = true
      this.dashboardStatsError = null
      try {
        const stats = await store.dispatch('panel/getDashboardStatsRequest')
        const requiredValues = [
          stats?.users?.totalUsers,
          stats?.users?.pendingVerification,
          stats?.transactions?.totalTransactions,
          stats?.transactions?.successfulTransactions,
          stats?.transactions?.successfulRialiAmount,
          stats?.transactions?.todayRialiAmount,
          stats?.transactions?.yesterdayRialiAmount,
          stats?.commissions?.totalRiali,
          stats?.commissions?.todayRiali,
          stats?.commissions?.yesterdayRiali,
          stats?.commissions?.totalUsdt
        ]
        if (!requiredValues.every(value => typeof value === 'number' && Number.isFinite(value))) {
          throw new Error('Invalid dashboard stats response')
        }
        this.dashboardStats = stats
      } catch (error) {
        console.error('Error loading dashboard stats:', error)
        this.dashboardStatsError = 'بارگذاری آمار داشبورد ناموفق بود. اتصال بک‌اند را بررسی و دوباره تلاش کنید.'
      } finally {
        this.dashboardStatsLoading = false
      }
    },
    async loadManualChargeStats() {
      this.manualChargeStatsLoading = true
      this.manualChargeStatsError = null
      try {
        this.manualChargeStats = await store.dispatch('panel/getManualChargeStats')
      } catch (error) {
        console.error('Error loading manual charge stats:', error)
        this.manualChargeStatsError = 'بارگذاری آمار شارژهای دستی ناموفق بود.'
      } finally {
        this.manualChargeStatsLoading = false
      }
    },
    formatShortAmount(formatted) {
      return String(formatted ?? '0').replace(' ریال', '').replace(' USDT', '')
    },
    goToManualCharges() {
      this.$router.push('/panel/manual-charges')
    }
  }
}
</script>
